/**
 * TanStack Query Hooks for Tenant Report Downloads
 *
 * This module provides hooks for downloading various tenant report files:
 * - Multireports (standard and express)
 * - Guarantor multireports
 * - Folder zips
 * - Individual files
 */
import { useMutation } from "@tanstack/react-query";

import {
  downloadGuarantorMultireportExpress,
  downloadTenantFolderZip,
  downloadTenantMultireport,
  downloadTenantMultireportExpress,
  getFile,
} from "../api/tenants";
import { toast } from "../utils/toast";

/**
 * Helper function to trigger file download in browser
 */
const triggerDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Download tenant multireport
 * Returns blob data that should be handled by the component
 */
export const useDownloadTenantMultireport = () => {
  return useMutation({
    mutationFn: async (reportId: string) => {
      const response = await downloadTenantMultireport(reportId);
      return response.data;
    },
    onSuccess: (blob, reportId) => {
      triggerDownload(blob, `multireporte-inquilino-${reportId}.pdf`);
      toast.success("Multireporte descargado exitosamente");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al descargar el multireporte";
      toast.error(errorMessage);
    },
  });
};

/**
 * Download tenant multireport express
 */
export const useDownloadTenantMultireportExpress = () => {
  return useMutation({
    mutationFn: async (reportId: string) => {
      const response = await downloadTenantMultireportExpress(reportId);
      return response.data;
    },
    onSuccess: (blob, reportId) => {
      triggerDownload(blob, `multireporte-express-${reportId}.pdf`);
      toast.success("Multireporte express descargado exitosamente");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        "Error al descargar el multireporte express";
      toast.error(errorMessage);
    },
  });
};

/**
 * Download guarantor multireport express
 */
export const useDownloadGuarantorMultireportExpress = () => {
  return useMutation({
    mutationFn: async (reportId: string) => {
      const response = await downloadGuarantorMultireportExpress(reportId);
      return response.data;
    },
    onSuccess: (blob, reportId) => {
      triggerDownload(blob, `multireporte-aval-${reportId}.pdf`);
      toast.success("Multireporte de aval descargado exitosamente");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        "Error al descargar el multireporte del aval";
      toast.error(errorMessage);
    },
  });
};

/**
 * Download tenant folder as zip
 */
export const useDownloadTenantFolderZip = () => {
  return useMutation({
    mutationFn: async (tenantReportId: string) => {
      return await downloadTenantFolderZip(tenantReportId);
    },
    onSuccess: (blob, tenantReportId) => {
      triggerDownload(blob, `carpeta-inquilino-${tenantReportId}.zip`);
      toast.success("Carpeta descargada exitosamente");
    },
  });
};

/**
 * Download a specific file from a tenant report
 */
export const useDownloadTenantReportFile = () => {
  return useMutation({
    mutationFn: async ({
      tenantReportId,
      fileId,
      fileName,
    }: {
      tenantReportId: string;
      fileId: string;
      fileName?: string;
    }) => {
      const blob = await getFile(tenantReportId, fileId);
      return { blob, fileName: fileName || `archivo-${fileId}` };
    },
    onSuccess: ({ blob, fileName }) => {
      triggerDownload(blob, fileName);
      toast.success("Archivo descargado exitosamente");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error al descargar el archivo";
      toast.error(errorMessage);
    },
  });
};
